import { createEcommerceEvent } from "../../tracking/create-ecommerce-event";
import { createEvent } from "../../tracking/create-event";

// REV-120: diagnostyczny wrapper łapiący nieudane wywołania zdarzeń (telemetria błędów trackingu).
export const eventMissingEvent = createEvent<{
  expectedEventName: string;
  errorContext: string;
}>("event_missing");

export function safeTrackEvent(eventFn: () => void, expectedEventName: string, errorContext: string) {
  try {
    eventFn();
  } catch (error) {
    try {
      eventMissingEvent({ expectedEventName, errorContext: `${errorContext}: ${String(error)}` });
    } catch {
      // Telemetry must never break the user flow.
    }
  }
}

// REV-99: wyświetlenie listy produktów w katalogu (strona główna + strona upsell).
export const viewItemListEvent = createEcommerceEvent("view_item_list");

// REV-100: dodanie produktu do koszyka z listingu/upsellu.
export const addToCartEvent = createEcommerceEvent<{ value: number }>("add_to_cart", {}, { includeIsUpsell: true });

// REV-103: usunięcie produktu z koszyka (usunięcie pozycji upsell).
export const removeFromCartEvent = createEcommerceEvent<{ value: number }>(
  "remove_from_cart",
  {},
  { includeIsUpsell: true },
);

// REV-101: odsłona strony wyboru terminu wizyty.
export const viewAvailableVisitDatesEvent = createEvent("view_available_visit_dates");

// REV-102: potwierdzenie wybranego terminu wizyty (klik „Przejdź dalej").
export const addPatientVisitDateEvent = createEvent<{
  visitDate: string;
  visitDateDay: string;
  visitDateHour: string;
}>("add_patient_visit_date");

// Parity with vouchers REV-88: fired when "Dalej" advances the user into the upsell page. In flows
// with an upsell, begin_checkout is deferred until the user leaves the upsell page, so this marks the
// forward transition into upsell that would otherwise be untracked.
export const goToUpsellEvent = createEvent("go_to_upsell");

// REV-104: rozpoczęcie checkoutu. Taki sam kształt jak begin_checkout w voucherach (bez indeksu itemu; value + numberOfProducts).
export const beginCheckoutEvent = createEcommerceEvent<{ value: number }, { numberOfProducts: number }>(
  "begin_checkout",
  {},
  { includeIndex: false },
);

// REV-105: odsłona strony weryfikacji telefonu (formularz danych pacjenta).
export const addPatientViewEvent = createEvent("add_patient_view");

// REV-106: poprawne wysłanie formularza danych pacjenta (klik „Przejdź dalej" z poprawnymi danymi).
export const addPatientEvent = createEvent<{
  yearOfBirth: number | null;
  gender: "female" | "male" | null;
  newsletterConsent: boolean;
  phoneCallConsent: boolean;
}>("add_patient");

// REV-107: wysłanie formularza pacjenta zablokowane przez walidację lub błąd backendu.
export const addPatientErrorEvent = createEvent<{ errorType: string }>("add_patient_error");

// REV-118: odsłona strony rejestracji (telefon nierozpoznany → standardowa ścieżka nowego konta).
export const signUpViewEvent = createEvent("sign_up_view");

// REV-109: rejestracja zakończona sukcesem. user_id to uuid pacjenta zwrócony przez endpoint rejestracji
// (REV-145), albo null, gdy pacjent ma restricted_app_access.
export const signUpEvent = createEvent<{ userId: string | null }>("sign_up");

// REV-110: rejestracja nieudana.
export const signUpErrorEvent = createEvent<{ errorType: string }>("sign_up_error");

// REV-108: zgoda na newsletter zaakceptowana w momencie utrwalenia rejestracji.
export const signUpNewsletterEvent = createEvent("sign_up_newsletter");

// REV-119: odsłona strony weryfikacji kodem (login = rozpoznane konto, simplify-register = gość).
export const verificationViewEvent = createEvent<{ method: "account" | "guest" }>("verification_view");

// REV-111: kod zaakceptowany, użytkownik przechodzi dalej. user_id to uuid pacjenta zwrócony przez endpoint
// auth (REV-145) dla weryfikacji „account", albo null dla gości / restricted_app_access.
export const verificationSuccessEvent = createEvent<{
  userId: string | null;
  method: "account" | "guest";
}>("verification_success");

// REV-112: kod weryfikacyjny odrzucony.
export const verificationErrorEvent = createEvent<{
  errorType: string;
  method: "account" | "guest";
}>("verification_error");

// REV-113: użytkownik kliknął „wyślij kod ponownie".
export const verificationResendCodeEvent = createEvent<{ method: "account" | "guest" }>("verification_resend_code");

// User type captured at registration/verification time and replayed on payment + purchase events.
export type UserType = "new_registered" | "existing" | "guest";

// REV-114: odsłona strony podsumowania zamówienia.
export const viewPaymentInfoEvent = createEcommerceEvent<
  { value: number },
  { numberOfProducts: number; userType: UserType | null }
>("view_payment_info", {}, { includeIndex: false });

// REV-115: klik „Opłać zamówienie".
export const addPaymentInfoEvent = createEcommerceEvent<
  { value: number; transactionId: string },
  { paymentType: string; numberOfProducts: number; userType: UserType | null }
>("add_payment_info", {}, { includeIndex: false });

// REV-117: montaż strony TYP (podziękowania za zakup). paymentType jest opcjonalne — darmowe zamówienia
// pomijają PayU i nie mają metody płatności; pominięcie pola utrzymuje wymiar GA4 zgodny z prawdą.
export const purchaseEvent = createEcommerceEvent<
  { value: number; transactionId: string },
  { paymentType?: string; numberOfProducts: number; userType: UserType | null }
>("purchase", {}, { includeIndex: false });
